import qs from 'qs'
import algoliasearch from 'algoliasearch'
import { isEmpty, pathOr } from 'ramda'
import { reverseGeocode, autocompleteCity } from '@lib/geo-api'
import {
  BOOK_THIS_WEEK_FILTER_LABEL,
  isBookThisWeekParamEnabled,
} from '@lib/coaches-directory/book-this-week'
import {
  applyPersonaPreset,
  stripPersonaPreset,
  isValidPersonaId,
} from './PopularGoals/persona-refinement-presets'

export const searchClient = algoliasearch(
  process.env.ALGOLIA_APP_ID,
  process.env.ALGOLIA_API_KEY,
)

export const updateAfterMilliseconds = 700

/**
 * Strip the refinement values a persona chip contributes from a searchState
 * before URL serialization, while keeping the `persona` id so the URL stays
 * shareable. Caller must pass `refinerData`; without it we fall back to the
 * raw searchState (no stripping) so the URL is still functionally correct.
 */
export const stripPersonaPresetForURL = (searchState, refinerData) => {
  if (!searchState?.persona || !isValidPersonaId(searchState.persona) || !refinerData) {
    return searchState
  }
  const personaId = searchState.persona
  const stripped = stripPersonaPreset(searchState, personaId, refinerData)
  return { ...stripped, persona: personaId }
}

export const omitDirectoryConfigureFilters = (searchState) => {
  if (!searchState?.configure || typeof searchState.configure !== 'object') {
    return searchState
  }
  const { filters, ...configure } = searchState.configure
  const next = { ...searchState }
  if (Object.keys(configure).length) next.configure = configure
  else delete next.configure
  return next
}

export const createURL = (state) => `?${qs.stringify(state)}`

const plusToSpace = (str) => str.replace('+', ' ')

const getPlacesFromUrl = async (params, aroundLatLng, aroundRadius) => {
  const placeQuery = params.length ? params.reduce((acc, cv) => `${acc} ${plusToSpace(cv)}`, '') : null
  if (placeQuery) {
    const [city] = await autocompleteCity(placeQuery)
    if (!city) return []
    return [
      {
        cityName: city.city,
        stateName: city.state,
        country: 'US',
        _geoloc: city._geoloc,
      },
    ]
  }
  if (aroundLatLng) {
    const city = await reverseGeocode(aroundLatLng)
    if (!city) return []
    return [{
      cityName: city.city,
      stateName: city.state,
      country: 'US',
      _geoloc: city._geoloc,
      ...(aroundRadius && { aroundRadius }),
    }]
  }
  return []
}

export const pathToSearchState = async (path, params, refinerData) => {
  const segments = params || []
  const parsed = path.includes('?') ? qs.parse(path.substring(path.indexOf('?') + 1)) : {}
  const placesRes = await getPlacesFromUrl(segments, parsed.aroundLatLng, parsed.aroundRadius)
  const baseState = {
    ...(!isEmpty(parsed) && { ...parsed }),
    ...(placesRes.length && { aroundLatLng: `${placesRes[0]._geoloc.lat}, ${placesRes[0]._geoloc.lng}` }),
    ...(placesRes.length && { placeName: `${placesRes[0].cityName}, ${placesRes[0].stateName}` }),
    ...(placesRes.length && { aroundRadius: placesRes[0].aroundRadius }),
  }

  if (Object.prototype.hasOwnProperty.call(baseState, 'bookThisWeek')) {
    if (isBookThisWeekParamEnabled(baseState.bookThisWeek)) {
      baseState.bookThisWeek = true
    } else {
      delete baseState.bookThisWeek
    }
  }

  const withoutStaleFilters = omitDirectoryConfigureFilters(baseState)

  if (withoutStaleFilters.persona && refinerData && isValidPersonaId(withoutStaleFilters.persona)) {
    return applyPersonaPreset(withoutStaleFilters, withoutStaleFilters.persona, refinerData)
  }
  return withoutStaleFilters
}

export const searchStateToURL = async (searchState, aroundLatLng, aroundRadius, refinerData) => {
  const stripped = omitDirectoryConfigureFilters(stripPersonaPresetForURL(searchState, refinerData))
  const { bookThisWeek, ...rest } = stripped || {}
  const searchStateWithCoords = {
    ...rest,
    ...(isBookThisWeekParamEnabled(bookThisWeek) && { bookThisWeek: true }),
    ...(aroundLatLng && { aroundLatLng }),
    ...(aroundRadius && { aroundRadius }),
  }
  return `${window.location.pathname}?${qs.stringify(searchStateWithCoords)}`
}

export const DEFAULT_PROPS = {
  searchClient,
  indexName: process.env.ALGOLIA_COACH_INDEX_NAME,
}

const flattenArrToStr = (arr = []) => {
  if (!arr.length) return ''
  if (typeof arr === 'string') return arr
  return arr.reduce((acc, cv) => {
    if (!isEmpty(acc)) {
      return `${acc}, ${cv}`
    }
    return cv
  }, '')
}

const getQueryAndLocation = (query, placeName) => {
  if (query && placeName) {
    return `${query} in ${placeName}`
  }
  if (query) return query
  if (placeName) return placeName
  return 'No Query Entered'
}

const EXPERIENCE_TYPE_TO_LABEL = {
  'Getting Started': 'Beginners',
  'Having More Fun': 'Fun',
  'Improving Your Game': 'Competitive',
}

const SERVICE_NAME_TO_LABEL = {
  Individual: '1:1 lesson',
  Group: 'Group lesson',
  Adult: 'Adults',
  Senior: 'Adults',
  Youth: 'Juniors',
  Teen: 'Juniors',
  'Playing with disabilities': 'Playing with Disabilities',
}

const genderMapperRefiners = {
  F: 'Coach Gender - Female',
  M: 'Coach Gender - Male',
}

const genderMapperFilters = {
  F: 'Female coach',
  M: 'Male coach',
}

const buildExperienceNameToLabel = (refinerData) => {
  const experienceTypes = pathOr([], ['Experiences', 'experienceTypes'], refinerData)
  return experienceTypes.reduce((acc, et) => {
    const label = EXPERIENCE_TYPE_TO_LABEL[et.typeName]
    if (!label) return acc;
    (et.experiences || []).forEach((exp) => {
      acc[exp.experienceName] = label
    })
    return acc
  }, {})
}

export const makeGaFriendlyObj = (searchState, refinerData) => {
  const {
    query, placeSearchValue, placeName, persona, toggle: { bookable } = {},
    bookThisWeek,
  } = searchState

  const experiencesArr = pathOr([], ['refinementList', 'experienceTypes.experiences.experienceName'], searchState)
  const servicesArr = pathOr([], ['refinementList', 'serviceTypes.services.serviceName'], searchState)
  const coachGenderArr = pathOr([], ['refinementList', 'gender'], searchState)
  const memberTypeArr = pathOr([], ['refinementList', 'memberType'], searchState)
  const bookableArr = [
    ...(bookable ? ['Book now'] : []),
    ...(isBookThisWeekParamEnabled(bookThisWeek) ? [BOOK_THIS_WEEK_FILTER_LABEL] : []),
  ]

  const memberTypeArrFriendly = memberTypeArr.length > 0
    ? ['PGA of America Member']
    : []

  const coachGenderArrFriendlyRefiners = coachGenderArr.length > 0
    ? coachGenderArr.map((g) => genderMapperRefiners[g])
    : []

  const coachGenderArrFriendlyFilters = coachGenderArr.length > 0
    ? coachGenderArr.map((g) => genderMapperFilters[g])
    : []

  const coachSearchRefiners = flattenArrToStr(
    experiencesArr.concat(servicesArr).concat(coachGenderArrFriendlyRefiners).concat(bookableArr).concat(memberTypeArrFriendly),
  )

  const experienceNameToLabel = buildExperienceNameToLabel(refinerData)
  const experienceLabels = [...new Set(experiencesArr?.length > 0 ? experiencesArr.map((name) => experienceNameToLabel[name]).filter(Boolean) : [])]
  const serviceLabels = [...new Set(servicesArr?.length > 0 ? servicesArr.map((name) => SERVICE_NAME_TO_LABEL[name]).filter(Boolean) : [])]

  const coachSearchFilters = flattenArrToStr(
    experienceLabels.concat(serviceLabels).concat(coachGenderArrFriendlyFilters).concat(bookableArr).concat(memberTypeArrFriendly),
  )

  return {
    coachSearchQuery: getQueryAndLocation(query, placeSearchValue || placeName),
    coachSearchRefiners: coachSearchRefiners || null,
    coachSearchFilters: coachSearchFilters || null,
    coachSearchPersona: persona || null,
  }
}

export const shouldShowEventsBanner = async () => {
  const algoliaGroupEventIndex = searchClient.initIndex(
    process.env.ALGOLIA_GROUP_EVENTS_INDEX_NAME,
  )
  const sixtyDaysInSeconds = 60 * 24 * 60 * 60
  const twentyMilesInMeters = 20 * 1609
  const { hits } = await algoliaGroupEventIndex.search('', {
    aroundLatLngViaIP: true,
    aroundRadius: twentyMilesInMeters,
    filters: `start_date_time_ts > ${Math.floor(
      Date.now() / 1000,
    )} AND start_date_time_ts < ${
      Math.floor(Date.now() / 1000) + sixtyDaysInSeconds
    } AND registration_closed:false AND is_canceled:false`,
  })
  return hits.length > 0
}
